// services/frontend/src/components/composed/CommentFormSection/CommentFormSection.tsx
// Feature 649: Component that encapsulates the comment form and header

import { Heading } from '../../atoms/Heading/Heading';
import { Text } from '../../atoms/Text/Text';
import { CommentForm } from '../CommentForm/CommentForm';
import type { CommentFormSubmitData } from '../CommentForm/CommentForm';
import type { Photo } from '../../../types/photo';

export interface CommentFormSectionProps {
  /** Callback when comment is submitted */
  onSubmit: (data: CommentFormSubmitData) => Promise<void>;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
  /** Error message to display */
  error?: string;
  /** Collection ID for autocomplete context */
  collectionId?: string;
  /** Session ID for participant autocomplete */
  sessionId?: string;
  /** Photo object for edit/markup modals */
  photo?: Photo;
  /** Whether this is an AI-only session */
  isAiSession?: boolean;
  /** Whether current user is the session/collection owner */
  isOwner?: boolean;
}

export function CommentFormSection({
  onSubmit,
  isSubmitting = false,
  error,
  collectionId,
  sessionId,
  photo,
  isAiSession = false,
  isOwner = false,
}: CommentFormSectionProps) {
  // AI sessions are view-only for non-owners
  if (isAiSession && !isOwner) {
    return (
      <div className="bg-muted/10 border border-muted/20 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2 text-muted">
          {/* Sparkles icon */}
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
          <span className="text-sm font-medium">AI Critique Session - View Only</span>
        </div>
        <Text variant="muted" className="mt-1 text-xs">
          This is an AI-only critique session. Comments are generated by AI personas.
        </Text>
      </div>
    );
  }

  return (
    <div>
      <Heading level="h5" className="mb-2">Add a Comment</Heading>
      <CommentForm
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        error={error}
        collectionId={collectionId}
        sessionId={sessionId}
        photo={photo}
      />
    </div>
  );
}
